<script lang="ts">
  import { buildTableRowActionSet } from "@kontextwerk/web-sdk/core/layout/table"

  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import StatusTag from "@kontextwerk/web-sdk/ui/display/StatusTag.svelte"
  import Table, { type TableGetRows } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import { getSortedTablePage } from "@kontextwerk/web-sdk/core/layout/tableSort"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiDelete, mdiPencil, mdiPlus } from "@mdi/js"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { deleteContactPoint } from "../../../../../../../../../../../../api/kehrblatt/customer-registry/contact-points"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import { openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import {
    buildDeleteContactPointApprovalDescription,
    CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS,
    customerRegistryActionTooltips,
  } from "../../../../../../../../_shared/_logic/customer-registry-action-tooltips"
  import ContactChannelsCell from "./_modules/contact-channels-cell"
  import type { CustomerContactRow } from "./_logic/contract"
  import {
    buildContactRows,
    contactColumns,
    contactKindLabel,
    contactKindTag,
    contactValueLines,
    importSyncTag,
    sourceTag,
    verificationTag,
  } from "./_logic/tableModel"
  import CustomerContactOverlay from "./_modules/customer-contact-overlay"

  interface Props {
    contacts: ContactPoint[]
    organizationId: string
    customerId: string
    canEdit: boolean
    onChange?: (contacts: ContactPoint[]) => void
    onChanged?: (contacts: ContactPoint[]) => void
  }

  let {
    contacts,
    organizationId,
    customerId,
    canEdit,
    onChange = undefined,
    onChanged = undefined,
  }: Props = $props()

  const notifyChange = (next: ContactPoint[]) => {
    if (typeof onChange === "function") onChange(next)

    if (typeof onChanged === "function") onChanged(next)
  }

  let refreshTable: (() => void) | null = null
  const numberFormatter = new Intl.NumberFormat("de-DE")
  const titleWithCount = $derived.by(
    () => `Kontaktdaten (${numberFormatter.format(contactRows.length)})`,
  )

  const contactRows: CustomerContactRow[] = $derived(buildContactRows(contacts))

  const getRows: TableGetRows = (params) =>
    getSortedTablePage({
      rows: contactRows,
      columns: contactColumns,
      sort: (params as { sort?: unknown }).sort as any,
      limit: params.limit,
      offset: params.offset,
    })

  const handleContactSaved = (contact: ContactPoint) => {
    const exists = contacts.some((entry) => entry.id === contact.id)
    contacts = exists
      ? contacts.map((entry) => (entry.id === contact.id ? contact : entry))
      : [contact, ...contacts]
    refreshTable?.()
    notifyChange(contacts)
  }

  const openContactOverlay = (contact?: ContactPoint) => {
    if (!canEdit || !organizationId || !customerId) return

    openOverlay({
      title: contact ? "Kontaktpunkt bearbeiten" : "Kontaktpunkt hinzufügen",
      content: CustomerContactOverlay,
      properties: {
        organizationId,
        customerId,
        contact: contact ?? null,
        onSaved: handleContactSaved,
      },
    })
  }

  const removeContact = async (contact: ContactPoint) => {
    if (!canEdit) return

    await newApproval({
      title: "Kontaktpunkt löschen",
      description: buildDeleteContactPointApprovalDescription(
        contact.label || contactKindLabel(contact.kind),
      ),
      approvalLabel: "Ja, löschen",
      declineLabel: "Abbrechen",
      approvalCallback: async () => {
        try {
          await deleteContactPoint(contact.id)
          contacts = contacts.filter((entry) => entry.id !== contact.id)
          refreshTable?.()
          notifyChange(contacts)
          newNotification({
            class: "success",
            html: "Kontaktpunkt wurde gelöscht.",
          })
        } catch (err) {
          console.error("contact_point_delete_failed", err)
          newNotification({
            class: "error",
            html: "Kontaktpunkt konnte nicht gelöscht werden.",
          })
        }
      },
    })
  }
</script>

<SectionTitle
  title={titleWithCount}
  brightBackground={true}
  helpModalTextId="customerDetailContacts"
>
  {#snippet buttonSnippet()}
    <CTA
      type="secondaryButton"
      content="Kontaktpunkt"
      tooltipContent={customerRegistryActionTooltips.contactPointAdd}
      tooltipClassName={CUSTOMER_REGISTRY_ACTION_TOOLTIP_CLASS}
      onClick={() => openContactOverlay()}
    >
      {#snippet iconSnippet()}
        <Icon path={mdiPlus} />
      {/snippet}
    </CTA>
  {/snippet}
</SectionTitle>

<Table
  rowActions={{
    resolve: (row, rowIndex) =>
      buildTableRowActionSet(
        ((row) => {
          const actions: import("@kontextwerk/web-sdk/core/layout/table").TableRowAction[] = []

          if (canEdit) {
            const contactRow = row as CustomerContactRow

            actions.push({
              id: "action-0",
              label: "Bearbeiten",
              onClick: () => openContactOverlay(contactRow.contact),
              icon: mdiPencil,
              tooltip: customerRegistryActionTooltips.contactPointEdit,
            })

            actions.push({
              id: "action-1",
              label: "Löschen",
              onClick: () => removeContact(contactRow.contact),
              icon: mdiDelete,
              tooltip: customerRegistryActionTooltips.contactPointDelete,
              tone: "danger",
            })
          }

          return actions
        })(row) ?? [],
        { primaryId: "action-0", inlineIds: [] },
      ),
  }}
  columns={contactColumns}
  {getRows}
  refreshKey={contactRows.map((row) => row.id).join("|")}
  theme="light"
  mode="table"
  showActions={canEdit}
  actionsHeaderLabel="Aktionen"
  urlStateId={`kehrblatt-customer-contacts-${customerId}`}
  setRefreshFunction={(fn) => {
    refreshTable = fn
  }}
>
  {#snippet tableCell({ row, column })}
    {@const contactRow = row as CustomerContactRow}
    {@const contact = contactRow.contact}
    {#if column.id === "kindLabel"}
      {@const tag = contactKindTag(contact.kind)}
      <StatusTag
        variant="neutral"
        size="sm"
        label={tag.label}
        color={tag.color}
        tooltipContent={tag.tooltip}
      />
    {:else if column.id === "channelsLabel"}
      <ContactChannelsCell {contact} />
    {:else if column.id === "verifiedLabel"}
      {@const tag = verificationTag(contact)}
      <StatusTag size="sm" label={tag.label} color={tag.color} tooltipContent={tag.tooltip} />
    {:else if column.id === "sourceLabel"}
      {@const tag = sourceTag(contact)}
      {@const syncTag = importSyncTag(contact)}
      <span class="contact-meta-tags">
        <StatusTag
          variant="neutral"
          size="sm"
          label={tag.label}
          color={tag.color}
          tooltipContent={tag.tooltip}
        />
        {#if syncTag}
          <StatusTag
            variant="neutral"
            size="sm"
            label={syncTag.label}
            color={syncTag.color}
            tooltipContent={syncTag.tooltip}
          />
        {/if}
      </span>
    {:else if column.id === "value"}
      <span class="contact-value" class:contact-value--address={contact.kind === "ADDRESS"}>
        {#each contactValueLines(contact) as line, idx (`${contact.id}-value-${idx}`)}
          <span class="contact-value__line">{line}</span>
        {/each}
      </span>
    {:else}
      {(row as Record<string, unknown>)[column.id] ?? ""}
    {/if}
  {/snippet}
</Table>

<style lang="less">
  .contact-value {
    display: inline-flex;
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  .contact-value--address {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    line-height: 1.25;
  }

  .contact-value__line {
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  .contact-meta-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    align-items: flex-start;
  }
</style>
